import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import applicationService from "../../services/application/application.service";
import { useQuery } from "@tanstack/react-query";
import {
  RiskDocumentAnswerDto,
  RiskLevelEnum,
} from "../../services/risk-document-answers/risk-document-answers.dto";
import { useRef, useState } from "react";
import { StandardType } from "../../services/standards/standards.dto";
import ArfDataTable, { ColumnMeta } from "../../components/table/ArfDataTable";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import riskDocumentAnswerService from "../../services/risk-document-answers/risk-document-answers.service";
import { useAxiosContext } from "../..";
import { ApplicationStatus } from "../../services/application/application.dto";
import { useAuth } from "../../utils/auth";
import { ApplicationRoles } from "../../services/roles/roles.dto";
type GroupedDocuments = Record<string, RiskDocumentAnswerDto[]>;

const RiskEvaluation = () => {
  const { id } = useParams();
  const auth = useAuth();
  const user = auth.getUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toaster = useAxiosContext();
  const menu = useRef<Menu>(null);
  const [selectedRiskDocument, setSelectedRiskDocument] = useState<RiskDocumentAnswerDto>();

  const { data: applicationForm } = useQuery({
    queryKey: ["applicationForm", id],
    queryFn: () =>
      applicationService.GetByUid(id || "", {
        blockUI: true,
      }),
    enabled: !!id,
  });
  const riskDocumentsQueryKey = ["risk-documents", id || ""];
  const { data: riskDocuments } = useQuery({
    queryKey: riskDocumentsQueryKey,
    queryFn: () =>
      riskDocumentAnswerService.GetByApplicationID(id || "", {
        blockUI: true,
      }),
    enabled: !!id,
    refetchOnMount: true,
  });

  const groupedDocuments = riskDocuments?.result?.applicationRiskDocuments.reduce<GroupedDocuments>(
    (acc, doc) => {
      const key = doc.riskTemplateDocument.standard.name;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(doc);
      return acc;
    },
    {}
  );

  const columns: ColumnMeta[] = [
    {
      field: "attachmentFile",
      header: t("File"),
      body: (row: RiskDocumentAnswerDto) => {
        return (
          <a
            href={`${process.env.REACT_APP_BASE_URL}files/${row.riskTemplateDocument.fileId}?mode=inline`}
            target="_blank"
            className="text-color hover-opacity-70 cursor-pointer"
          >
            {row.riskTemplateDocument.file?.name}
          </a>
        );
      },
    },
    {
      field: "riskLevel",
      header: t("RiskLevel"),
      body: (row: RiskDocumentAnswerDto) => {
        return (
          <span
            className={`${
              row.riskLevel === RiskLevelEnum.Low
                ? "riskevaluation-status"
                : row.riskLevel === RiskLevelEnum.Medium
                ? "contractsent-status"
                : row.riskLevel === RiskLevelEnum.High
                ? "sentforrevision-status"
                : "preapplication-status"
            } status-container white-space-nowrap`}
          >
            {row.riskLevel ? t(RiskLevelEnum[row.riskLevel]) : t("NotAssessed")}
          </span>
        );
      },
    },

    {
      field: "answerFile",
      header: t("Status"),
      body: (row: RiskDocumentAnswerDto) => {
        return (
          <div className={`${getRiskDocumentStatusClass(row)} status-container`}>
            {t(getRiskDocumentStatusText(row))}
          </div>
        );
      },
      style: {
        width: "20rem",
      },
    },
    {
      field: "actions",
      header: "Actions",
      body: row => {
        return (
          <>
            <Button
              icon="pi pi-ellipsis-h"
              rounded
              text
              onClick={e => {
                setSelectedRiskDocument(row);
                menu?.current?.toggle(e);
              }}
            />
          </>
        );
      },
      style: {
        width: "3rem",
      },
    },
  ];

  var actions: MenuItem[] = [
    {
      label: t("Detail"),
      icon: "pi pi-search",
      command: () => {
        if (applicationForm && selectedRiskDocument) {
          navigate(`/app/risk-evaluation/detail/${selectedRiskDocument.id}`, {
            state: {
              selectedRiskDocument: selectedRiskDocument,
              applicationForm: applicationForm.result,
              riskDocumentsQueryKey: riskDocumentsQueryKey,
            },
          });
        }
      },
    },
  ];

  const moveToReview = () => {
    if (applicationForm?.result?.uid) {
      riskDocumentAnswerService
        .MoveToReview(applicationForm.result.uid, {
          blockUI: true,
        })
        .then(response => {
          if (!response.error) {
            toaster.showMessage(t("RiskEvaluationMovedToReview"), "success");
            navigate(`/app/duties/detail/${applicationForm?.result?.id}`);
          }
        });
    }
  };

  const completeRiskEvaluation = () => {
    if (applicationForm?.result?.uid) {
      riskDocumentAnswerService
        .CompleteRiskEvaluation(applicationForm.result.uid, {
          blockUI: true,
        })
        .then(response => {
          if (!response.error) {
            toaster.showMessage(t("RiskEvaluationCompleted"), "success");
            navigate(`/app/duties/detail/${applicationForm?.result?.id}`);
          }
        });
    }
  };

  const actionButtonVisible = () => {
    const allRiskDocumentsUploaded = riskDocuments?.result?.applicationRiskDocuments.every(
      doc => doc.applicationRiskDocumentAnswers && doc.applicationRiskDocumentAnswers.length > 0
    );
    const allRiskDocumentsReviewed = riskDocuments?.result?.applicationRiskDocuments.every(
      doc => doc.riskLevel
    );

    if (
      applicationForm?.result?.status === ApplicationStatus.RiskEvaluation &&
      (user.role === ApplicationRoles.SuperAdmin || user.role === ApplicationRoles.PlanningAdmin) &&
      allRiskDocumentsUploaded &&
      allRiskDocumentsReviewed
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="px-3 md:px-6 py-3 absolute w-full lg:relative">
      <div className="flex justify-content-between">
        <div
          className="flex flex-row gap-2 mb-3 w-max hover-opacity-70 cursor-pointer"
          onClick={() => navigate(`/app/duties/detail/${applicationForm?.result?.id}`)}
        >
          <div className="flex align-items-center">
            <i className="pi pi-arrow-left text-base"></i>
          </div>
          <div className="flex align-items-center font-medium text-xl">{t("RiskEvaluation")}</div>
        </div>
        <Button
          label={t("CompleteRiskEvaluation")}
          onClick={completeRiskEvaluation}
          visible={actionButtonVisible()}
        />
      </div>
      <Menu model={actions} popup ref={menu} />
      <div className="flex flex-column gap-2">
        {Object.entries(groupedDocuments || {}).map(([standardName, documents]) => (
          <div key={standardName} className="flex flex-column">
            <span className="standart-name-cnt">{standardName}</span>
            <ArfDataTable
              data={{
                data: documents,
                totalCount: documents.length,
              }}
              columns={columns}
              filterOptions={{
                enabled: false,
              }}
              actionOptions={{
                enabled: false,
              }}
              loading={false}
              exportOptions={{
                enabled: false,
              }}
              emptyMessageOptions={{
                text: t("EmptyRiskEvaluationTableMessage"),
                enabled: false,
              }}
              paginatorOptions={{
                enabled: false,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RiskEvaluation;

export const getRiskDocumentStatusClass = (riskDocument: RiskDocumentAnswerDto) => {
  if (
    riskDocument.applicationRiskDocumentAnswers &&
    riskDocument.applicationRiskDocumentAnswers.length > 0
  ) {
    if (riskDocument.riskLevel) {
      return "offerapproved-status";
    } else {
      return "paymentpending-status";
    }
  } else {
    return "inprogress-status";
  }
};

export const getRiskDocumentStatusText = (riskDocument: RiskDocumentAnswerDto) => {
  if (
    riskDocument.applicationRiskDocumentAnswers &&
    riskDocument.applicationRiskDocumentAnswers.length > 0
  ) {
    if (riskDocument.riskLevel) {
      return "Completed";
    }
    return "Uploaded";
  } else {
    return "PendingUpload";
  }
};
